<template>
    <x-container fluid class="manual-firm-order-container">
        <x-layout>
            <x-flex>
                <template v-if="loading">
                    <loading-spinner />
                </template>

                <template v-else>
                    <x-form v-if="manualFirmOrderStep === 1" v-model="firmOrderOffersFormValid">
                        <x-layout v-if="isCaptiveReinsurance">
                            <x-flex class="right">
                                <x-card class="panel mb-4">
                                    <x-card-title>
                                        <h3>Manual firm order</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <x-data-table
                                            class="captive-offer-table no-row-hover"
                                            :headers="captiveOfferedTableHeaders"
                                            :items="[captiveInsurerOffer]"
                                            hide-actions
                                        >
                                            <template v-slot:items="props">
                                                <tr>
                                                    <td class="included-column text-xs-center">
                                                        <x-checkbox
                                                            :input-value="props.item.is_selected"
                                                            hide-details
                                                            readonly
                                                        />
                                                    </td>

                                                    <td>
                                                        <strong>{{ props.item.insurer.name }}</strong>
                                                    </td>

                                                    <td class="text-xs-right">
                                                        N/A

                                                        <div v-if="showHorizontalPriceChange(0)">
                                                            <x-icon size="18">arrow_upward</x-icon>
                                                            {{ pricing.horizontalPrice }} BPS
                                                        </div>

                                                        <div v-if="showHorizontalPercentageChange(null, true)">
                                                            <x-icon size="18">arrow_upward</x-icon>
                                                            {{ pricing.horizontalPrice }} BPS
                                                        </div>
                                                    </td>

                                                    <td>
                                                        {{ getOfferTypeName(props.item.type) }}
                                                    </td>

                                                    <td>
                                                        <value-percent-split-input
                                                            v-model="props.item.written_line_percent"
                                                            :total="lineRequired"
                                                            :maxAmount="props.item.line_size_amount"
                                                            :currency-code="submission.currency.code"
                                                            :required="props.item.is_selected"
                                                            :percentage-rules="[v => ruleLessThanOrEqualTo(v, 50, 'Percentage')]"
                                                            :allowZero="true"
                                                            hide-details
                                                            no-margin
                                                            small
                                                        />
                                                    </td>

                                                    <td>
                                                        N/A
                                                    </td>

                                                    <td class="text-xs-center" :class="{ 'no-padding': !props.item.subjectivities.length }">
                                                        <warning-items
                                                            v-if="props.item.subjectivities.length > 0"
                                                            v-model="props.item.subjectivities"
                                                            item-text="name"
                                                            item-width="200"
                                                        />
                                                    </td>
                                                </tr>
                                            </template>
                                        </x-data-table>
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex class="right">
                                <x-card class="panel mb-4">
                                    <x-card-title v-if="!isCaptiveReinsurance">
                                        <h3>Manual firm order</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <x-data-table
                                            class="no-row-hover"
                                            :headers="offeredTableHeaders"
                                            :items="availableInsurerOffers"
                                            hide-actions
                                        >
                                            <template v-slot:no-data>
                                                <p class="error-message text-xs-center">No insurers</p>
                                            </template>
                                            <template v-slot:items="props">
                                                <tr>
                                                    <td>
                                                        {{ props.item.insurer.name }}
                                                    </td>

                                                    <td>
                                                        <x-text-field
                                                            v-model="props.item.brokerage"
                                                            suffix="%"
                                                            :rules="[
                                                                v => ruleRequired(v, 'Brokerage'),
                                                                v => ruleGreaterThanOrEqualTo(v, 0, 'Brokerage'),
                                                                v => ruleLessThanOrEqualTo(v, 100, 'Brokerage')
                                                            ]"
                                                            :class="{
                                                                'input-no-margin': 'no-margin',
                                                            }"
                                                            required
                                                            hide-details
                                                            no-margin
                                                        />
                                                    </td>

                                                    <td>
                                                        <x-select
                                                            :items="responseTypes"
                                                            v-model="props.item.type"
                                                            append-icon="keyboard_arrow_down"
                                                            placeholder="Select type"
                                                            item-text="name"
                                                            item-value="value"
                                                            :menu-props="{
                                                                offsetY: true,
                                                                maxHeight: 'none',
                                                                value: false,
                                                                openOnClick: true,
                                                                closeOnClick: true,
                                                                closeOnContentClick: true,
                                                                transition: 'scroll-y-transition'
                                                            }"
                                                            :class="{
                                                                'input-no-margin': 'no-margin',
                                                            }"
                                                            hide-details
                                                            @change="() => resetTypeValues(props.item.id)"
                                                        />
                                                    </td>

                                                    <td>
                                                        <template v-if="isRateOffer(props.item.type)">
                                                            <x-text-field
                                                                v-model="props.item.price"
                                                                suffix="BPS"
                                                                :rules="[
                                                                    v => ruleRequired(v, 'Quote price'),
                                                                    v => ruleGreaterThan(v, 0, 'Quote price')
                                                                ]"
                                                                :class="{
                                                                    'input-no-margin': 'no-margin',
                                                                }"
                                                                placeholder=" "
                                                                required
                                                                hide-details
                                                                no-margin
                                                            />

                                                            <div v-if="showHorizontalPriceChange(props.item.price)">
                                                                <x-icon size="18">arrow_upward</x-icon>

                                                                {{ pricing.horizontalPrice }} BPS
                                                            </div>
                                                        </template>

                                                        <template v-else-if="isFlatFeeOffer(props.item.type)">
                                                            <x-currency-text-field
                                                                v-model="props.item.flat_fee"
                                                                placeholder="Flat fee"
                                                                class="quote-price"
                                                                :prefix="submission.currency.code"
                                                                :rules="[
                                                                    v => ruleRequired(v, 'Flat fee'),
                                                                    v => ruleGreaterThan(props.item.flat_fee, 0, 'Flat fee')
                                                                ]"
                                                                :class="{
                                                                    'input-no-margin': 'no-margin',
                                                                    'input-small': 'small'
                                                                }"
                                                                required
                                                                hide-details
                                                                no-margin
                                                            />
                                                        </template>

                                                        <template v-else-if="isPercentageOfMarginOffer(props.item.type)">
                                                            <div class="d-flex child-flex split-input">
                                                                <value-percent-split-input
                                                                    v-if="haveMarginFieldDefined"
                                                                    v-model="props.item.percentage_of_margin"
                                                                    :amount-value.sync="props.item.price"
                                                                    :total="totalMargin"
                                                                    currency-code="BPS"
                                                                    amount-placeholder="Price"
                                                                    percentage-placeholder="% of margin"
                                                                    percentagePlaceholder=" "
                                                                    required
                                                                    hide-details
                                                                    no-margin
                                                                />

                                                                <x-text-field
                                                                    v-if="!haveMarginFieldDefined"
                                                                    v-model="props.item.percentage_of_margin"
                                                                    suffix="%"
                                                                    class="value-percentage-of-margin"
                                                                    placeholder="Percentage of margin"
                                                                    :rules="[
                                                                        v => ruleRequired(v, 'Percentage of margin'),
                                                                        v => ruleGreaterThan(v, 0, 'Percentage of margin'),
                                                                    ]"
                                                                    :class="{
                                                                        'input-no-margin': 'no-margin',
                                                                    }"
                                                                    hide-details
                                                                    required
                                                                />

                                                                <x-tooltip top>
                                                                    <template v-slot:activator="{ on }">
                                                                        <x-text-field
                                                                            v-if="!haveMarginFieldDefined"
                                                                            v-model="props.item.minimum_price"
                                                                            suffix="BPS"
                                                                            placeholder="Minimum price"
                                                                            class="value-minimum-price input-text-right"
                                                                            :rules="[
                                                                                v => ruleRequired(v, 'Minimum price'),
                                                                                v => ruleGreaterThanOrEqualTo(v, 0, 'Minimum price')
                                                                            ]"
                                                                            :class="{
                                                                                'input-no-margin': 'no-margin',
                                                                                'input-small': 'small'
                                                                            }"
                                                                            hide-details
                                                                            required
                                                                            v-on="on"
                                                                        />
                                                                    </template>

                                                                    <div>Minimum price</div>
                                                                </x-tooltip>
                                                            </div>

                                                            <div v-if="showHorizontalPercentageChange(props.item.percentage_of_margin)">
                                                                <x-icon size="18">arrow_upward</x-icon>

                                                                {{ pricing.horizontalPrice | numberNoTrail }} BPS
                                                            </div>
                                                        </template>

                                                        <template v-else> -- </template>
                                                    </td>

                                                    <td>
                                                        <template v-if="Boolean(props.item.type)">
                                                            <value-percent-split-input
                                                                v-model="props.item.written_line_percent"
                                                                :total="lineRequired"
                                                                :amount-value.sync="props.item.written_line_amount"
                                                                :currency-code="submission.currency.code"
                                                                required
                                                                hide-details
                                                                no-margin
                                                            />
                                                        </template>

                                                        <template v-else> -- </template>
                                                    </td>

                                                    <td>
                                                        <template v-if="Boolean(props.item.type)">
                                                            <x-select
                                                                v-model="props.item.waiting_period"
                                                                :items="filteredWaitingPeriods"
                                                                placeholder="Waiting period"
                                                                item-value="value"
                                                                item-text="name"
                                                                :rules="[ v => ruleRequired(props.item.waiting_period, 'Waiting period') ]"
                                                                append-icon="keyboard_arrow_down"
                                                                :menu-props="{ offsetY: true }"
                                                                :class="{
                                                                    'input-no-margin': 'no-margin'
                                                                }"
                                                                required
                                                                hide-details
                                                            />
                                                        </template>

                                                        <template v-else> -- </template>
                                                    </td>

                                                    <td class="text-xs-center">
                                                        <x-btn
                                                            class="square-icon extra-small"
                                                            color="tertiary"
                                                            @click="removeInsurer(props.item)"
                                                        >
                                                            <i class="material-icons">close</i>
                                                        </x-btn>
                                                    </td>
                                                </tr>

                                                <tr
                                                    v-if="Boolean(props.item.type) && offerSecurities(props.item).length > 0"
                                                    class="unstyled-row"
                                                >
                                                    <td />

                                                    <td colspan="6">
                                                        <x-layout
                                                            v-for="(line, lineIndex) in offerLines(props.item)"
                                                            :key="`security-line-${props.item.id}-${lineIndex}`"
                                                            align-end
                                                        >
                                                            <x-flex xs4>
                                                                <x-select
                                                                    :value="line.security_id"
                                                                    :items="getSecuritiesFormatted(lineSecurityOptions(props.item, lineIndex))"
                                                                    class="required"
                                                                    label="Security"
                                                                    placeholder="Security"
                                                                    append-icon="keyboard_arrow_down"
                                                                    :menu-props="{ offsetY: true }"
                                                                    hide-details
                                                                    @change="value => setLineSecurity(props.item, lineIndex, value)"
                                                                />
                                                            </x-flex>

                                                            <x-flex v-if="offerLines(props.item).length > 1" xs3 ml-3>
                                                                <x-text-field
                                                                    :value="line.written_line_percent"
                                                                    type="number"
                                                                    min="0"
                                                                    class="security-line-percent"
                                                                    label="Written line"
                                                                    suffix="%"
                                                                    hide-details
                                                                    :rules="[v => lineWrittenPercentRule(v)]"
                                                                    @input="value => setLineWrittenPercent(props.item, lineIndex, value)"
                                                                />
                                                            </x-flex>

                                                            <x-flex
                                                                v-if="offerLines(props.item).length > 1 && isFlatFeeOffer(props.item.type)"
                                                                shrink
                                                                ml-3
                                                                mb-1
                                                                class="security-line-flat-fee"
                                                            >
                                                                {{ lineFlatFee(props.item, line) | currency(submission.currency.code) }}
                                                            </x-flex>

                                                            <x-flex v-if="offerLines(props.item).length > 1" shrink ml-3 mb-1>
                                                                <x-btn
                                                                    class="square-icon extra-small ma-0"
                                                                    color="tertiary"
                                                                    @click="removeSecurityLine(props.item, lineIndex)"
                                                                >
                                                                    <i class="material-icons">close</i>
                                                                </x-btn>
                                                            </x-flex>
                                                        </x-layout>

                                                        <p
                                                            v-if="offerLines(props.item).length > 1 && !offerLinesSplitValid(props.item)"
                                                            class="error-message security-line-error"
                                                        >
                                                            Line percentages must each be above zero and total {{ props.item.written_line_percent }}%.
                                                        </p>

                                                        <x-btn
                                                            v-if="canAddSecurityLine(props.item)"
                                                            class="ma-0 mt-1"
                                                            color="primary"
                                                            outline
                                                            small
                                                            @click="addSecurityLine(props.item)"
                                                        >
                                                            Add security line
                                                        </x-btn>
                                                    </td>
                                                </tr>
                                            </template>
                                        </x-data-table>

                                        <x-container pa-0>
                                            <x-layout>
                                                <x-flex xs4>
                                                    <x-text-field
                                                        v-if="netMarginRequired"
                                                        v-model="localPricing.net_margin"
                                                        label="Net margin"
                                                        suffix="BPS"
                                                        :rules="[v => ruleGreaterThanOrEqualTo(v, 0, 'Net margin')]"
                                                    />
                                                    <x-text-field
                                                        v-if="grossMarginRequired"
                                                        v-model="localPricing.gross_margin"
                                                        label="Gross margin"
                                                        suffix="BPS"
                                                        :rules="[v => ruleGreaterThanOrEqualTo(v, 0, 'Gross margin')]"
                                                    />
                                                </x-flex>
                                            </x-layout>
                                        </x-container>
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>

                        <x-layout v-if="isCaptiveReinsurance">
                            <x-flex xs4 class="left">
                                <x-card class="panel mb-5">
                                    <x-card-text>
                                        <data-list v-model="captiveFirmOrderDataList" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>

                            <x-flex xs8>
                                <x-card class="panel mb-5">
                                    <x-card-text>
                                        <progress-linear-bar
                                            v-model="percentageRetained"
                                            class="mb-4"
                                            label="Captive"
                                            end-label="Reinsured"
                                            :bar-label="captiveInsurerOfferIsOffered ? undefined : 'PENDING'"
                                            bar-label-class="grey--text text--lighten-2"
                                            color="teal"
                                            :background-color="captiveInsurerOfferIsOffered ? 'teal' : undefined"
                                            :background-opacity="captiveInsurerOfferIsOffered ? 0.3 : undefined"
                                        />

                                        <progress-linear-bar
                                            v-model="percentageWritten"
                                            class="mb-4"
                                            label="Written"
                                            :end-label="$options.filters.percent(percentageWritten)"
                                            :end-label-color="getEndLabelColor(percentageWritten)"
                                            color="teal"
                                        />
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>

                        <x-layout v-else>
                            <x-flex xs4 class="left">
                                <x-card class="panel mb-5">
                                    <x-card-text>
                                        <data-list v-model="firmOrderDataList"/>
                                    </x-card-text>
                                </x-card>
                            </x-flex>

                            <x-flex xs8>
                                <x-card class="panel mb-5">
                                    <x-card-text>
                                        <x-switch
                                            v-model="localPricing.structure"
                                            class="double-switch mt-0 mb-4"
                                            label="Horizontal"
                                            true-value="vertical"
                                            false-value="horizontal"
                                            color="primary"
                                            hide-details
                                            @change="switched"
                                        >
                                            <template v-slot:append>Vertical</template>
                                        </x-switch>

                                        <progress-linear-bar
                                            v-model="percentageOffered"
                                            class="mb-4"
                                            label="Offered"
                                            :end-label="$options.filters.percent(percentageOffered)"
                                            :end-label-color="getEndLabelColor(percentageOffered)"
                                            color="teal"
                                        />

                                        <progress-linear-bar
                                            v-model="percentageWritten"
                                            class="mb-4"
                                            label="Written"
                                            :end-label="$options.filters.percent(percentageWritten)"
                                            :end-label-color="getEndLabelColor(percentageWritten)"
                                            color="teal"
                                        />
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>

                        <x-layout mt-3>
                            <x-flex xs6>
                                <x-btn @click="$emit('submission:navigate-to', currentStep - 1)" color="tertiary" class="mr-4">
                                    <i class="material-icons mr-2">arrow_back</i>

                                    {{ submission.id > 0 ? 'Back' : 'Cancel' }}
                                </x-btn>

                                <delete-quote-button v-if="submission.status === 'draft'" :submissionId="submission.id" />
                            </x-flex>

                            <x-flex xs6 text-xs-right>
                                <x-btn
                                    class="wide show"
                                    type="submit"
                                    color="primary"
                                    :disabled="nextButtonDisabled"
                                    @click="goToNextStep"
                                >
                                    Next

                                    <i class="material-icons ml-2">trending_flat</i>
                                </x-btn>
                            </x-flex>
                        </x-layout>
                    </x-form>

                    <firm-order-confirmation
                        v-show="manualFirmOrderStep === 2"
                        :submission.sync="submission"
                        :current-step="manualFirmOrderStep"
                        :pricing="pricing"
                        :draft-firm-order="draftFirmOrder"
                        :cities="cities"
                        @firm-order:navigate-to="goToStep"
                        @submission:complete="$emit('submission:complete', true)"
                        @standalonePolicy:manualFirmOrderDraft="$emit('standalonePolicy:manualFirmOrderDraft', $event)"
                        :selectedInsurersUsersList="selectedInsurersUsersList"
                        :isStandalonePolicy="isStandalonePolicy"
                        isManualFirmOrder
                        :firmOrder="firmOrder"
                    />

                    <broker-firm-order-summary
                        v-if="manualFirmOrderStep === 3"
                        :current-step="manualFirmOrderStep"
                        :firmOrder="firmOrder.data"
                        :selectedInsurersUsersList="selectedInsurersUsersList"
                        isManualFirmOrder
                        @firm-order:navigate-to="goToStep"
                    />

                    <modal
                        ref="signDown"
                        title="Sign down firm order"
                        confirmation
                    >
                        <p>Are you sure you want to sign down this firm order?</p>
                    </modal>
                </template>
            </x-flex>
        </x-layout>
    </x-container>
</template>

<script>
    import FirmOrderConfirmation from "~/components/Broker/FirmOrder/FirmOrderConfirmation";
    import StatusChip from '~/components/Shared/UIComponents/StatusChip';
    import ProgressLinearBar from '~/components/Broker/UIComponents/ProgressLinearBar';
    import ValuePercentSplitInput from '~/components/Shared/UIComponents/ValuePercentSplitInput';
    import CheckIcon from '~/components/Shared/UIComponents/CheckIcon';
    import DataList from '~/components/Shared/UIComponents/DataList';
    import WarningItems from '~/components/Shared/UIComponents/WarningItems';
    import Big from 'big.js';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import rules from '~/mixins/rules';
    import submissionHelpers from "../../../mixins/submissionHelpers";
    import BrokerFirmOrderSummary from '~/pages/broker-portal/BrokerRisk/BrokerFirmOrderSummary';
    import DeleteQuoteButton from '~/components/Broker/UIComponents/DeleteQuoteButton';

    export default {
        name: 'ManualFirmOrder',
        components: {
            FirmOrderConfirmation,
            StatusChip,
            ProgressLinearBar,
            ValuePercentSplitInput,
            CheckIcon,
            DataList,
            WarningItems,
            Modal,
            BrokerFirmOrderSummary,
            DeleteQuoteButton,
        },
        mixins: [rules, submissionHelpers],
        props: {
            submission: Object,
            selectedInsurersUsersList: Object,
            currentStep: Number,
            insurers: Array,
            waitingPeriods: Array,
        },
        data() {
            return {
                offeredTableHeaders: [
                    {
                        text: 'Insurer',
                        value: 'insurer.name',
                        width: '10%'
                    },
                    {
                        text: 'Brokerage',
                        value: 'brokerage',
                        sortable: false,
                        width: '10%'
                    },
                    {
                        text: 'Offer Type',
                        value: 'type',
                        sortable: false,
                        width: '20%'
                    },
                    {
                        text: 'Price',
                        value: '',
                        sortable: false,
                        width: '20%'
                    },
                    {
                        text: 'Written Line',
                        value: '',
                        sortable: false,
                        width: '25%'
                    },
                    {
                        text: 'Waiting Period',
                        value: 'waiting_period',
                        sortable: false,
                        width: '5%'
                    },
                    {
                        // remove insurer
                        text: 'Remove',
                        value: '',
                        sortable: false,
                        align: 'center'
                    }
                ],
                captiveOfferedTableHeaders: [
                    {
                        text: 'Include?',
                        value: 'is_selected',
                        align: 'center'
                    },
                    {
                        text: 'Insurer',
                        value: 'insurer.name'
                    },
                    {
                        text: 'Price Offered',
                        value: 'price',
                        align: 'right'
                    },
                    {
                        text: 'Offer Type',
                        value: 'type'
                    },
                    {
                        text: 'Written Line',
                        value: '',
                        sortable: false
                    },
                    {
                        text: 'Waiting Period',
                        value: 'waiting_period'
                    },
                    {
                        //warning items
                        text: '',
                        sortable: false,
                        class: 'no-padding',
                        align: 'center'
                    }
                ],
                loading: true,
                manualFirmOrderStep: 1,
                firmOrderOffersFormValid: false,
                pricing: {
                    structure: 'vertical',
                    horizontalPrice: 0,
                    horizontalPercentageOfMargin: 0,
                    net_margin: this.submission.net_margin,
                    gross_margin: this.submission.gross_margin,
                },
                draftFirmOrder: null,
                priceRules: [
                    value => value !== null || 'Please enter a price',
                    value => value !== '' || 'Please enter a price',
                    value => value !== 0 || 'Please enter a price',
                ],
                securityTypes: [],
                submissionComplete: false,
                openFirmOrderPanelIndex: 0,
                percentageDecimalPlaces: 12,
                cities: [],
                firmOrder: {},
            }
        },
        created() {
            this.fetchData();
            if (this.isCaptiveReinsurance) {
                this.pricing.structure = 'horizontal';
            }
        },
        computed: {
            isCaptiveReinsurance() {
                return this.submission.transaction_type === 'captive_reinsurance';
            },
            captiveFirmOrderDataList() {
                return [
                    {
                        text: 'Line required',
                        value: this.$options.filters.currency(this.lineRequired, this.submission.currency.code)
                    },
                    {
                        text: 'Retained limit',
                        value: this.retainedLimit !== null ? this.$options.filters.currency(this.retainedLimit, this.submission.currency.code) : 'Pending'
                    },
                    {
                        text: 'Reinsured limit',
                        value: this.reinsuredLimit !== null ? this.$options.filters.currency(this.reinsuredLimit, this.submission.currency.code) : 'Pending'
                    },
                    {
                        text: 'Outstanding',
                        value: this.captiveOutstanding
                    },
                    {
                        text: 'Best Price',
                        value: `${this.bestPrice} BPS`
                    },
                    {
                        text: 'Waiting Period',
                        value: `${this.waitingPeriod} days`
                    }
                ]
            },
            responseTypes() {
                const responseTypes = [
                    {
                        value: 'rate',
                        name: 'Offer (Rate)',
                        order: 11
                    },
                ];

                if (!this.isCaptiveReinsurance) {
                    responseTypes.push({
                        name: 'Offer (Flat fee)',
                        value: 'flat_fee',
                        order: 22
                    });
                }

                if (this.submission.product.key !== 'supply_chain_reinsurance') {
                    responseTypes.push(
                        {
                            value: 'percentage_of_net_margin',
                            name: 'Offer (Percentage of net margin)',
                            order: 33
                        },
                        {
                            value: 'percentage_of_gross_margin',
                            name: 'Offer (Percentage of gross margin)',
                            order: 34
                        },
                    );
                }

                return responseTypes.sort(function(a, b) {
                    return a.order - b.order;
                });
            },
            selectedInsurers() {
                const insurerIds = Object.keys(this.selectedInsurersUsersList).map(id => Number(id));
                let selectedInsurers = [];
                if (insurerIds.length > 0) {
                    selectedInsurers = this.insurers.filter(insurer => insurerIds.includes(insurer.id));
                }

                if (this.isCaptiveReinsurance) {
                    selectedInsurers.push(this.submission.captive_insurer_company);
                }

                return selectedInsurers;
            },
            firmOrderDataList() {
                return [
                    {
                        text: 'Line Required',
                        value: this.$options.filters.currency(this.lineRequired, this.submission.currency.code)
                    },
                    {
                        text: 'Outstanding',
                        value: this.$options.filters.currency(this.outstanding, this.submission.currency.code)
                    },
                    {
                        text: 'Best Price',
                        value: `${this.bestPrice} bps`
                    },
                    {
                        text: 'Waiting Period',
                        value: `${this.waitingPeriod} days`
                    }
                ]
            },

            hasValidDeclarationLines() {
                const validLines = this.selectedInsurerOffers.every(line => {
                    //check line type
                    if (!line.type) {
                        return false;
                    }

                    //check line price
                    if ((this.isTypeRate(line.type) && !line.price) ||
                        (this.isTypeFlatFee(line.type) && !line.flat_fee) ||
                        (this.isTypePercentageOfMargin(line.type) && !line.percentage_of_margin)
                    ) {
                        return false;
                    }

                    return true;
                });
                return this.isCaptiveReinsurance
                                ? validLines && this.hasSameLineTypes
                    : validLines && (this.localPricing.structure === 'vertical' || (this.localPricing.structure === 'horizontal' && this.hasSameLineTypes));
            },
            hasSameLineTypes() {
                return this.hasSameTypes(this.selectedInsurerOffers);
            },
            rateOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedInsurerOffers.filter(o => o.type === 'rate').length;
            },
            percentageOfMarginOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedInsurerOffers.filter(o => ['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(o.type)).length;
            },
            percentageOfNetMarginOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedInsurerOffers.filter(o => o.type === 'percentage_of_net_margin').length;
            },
            percentageOfGrossMarginOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedInsurerOffers.filter(o => o.type === 'percentage_of_gross_margin').length;
            },
            flatFeeOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedInsurerOffers.filter(o => o.type === 'flat_fee').length;
            },
            percentageOffered() {
                if (this.selectedOffers.length < 1) {
                    return 0;
                }

                const line = this.selectedOffers.map(function (offer) {
                    // check written percent is a valid number, otherwise interpret as zero
                    if (offer.line_size_percent && !isNaN(offer.line_size_percent)) {
                        return parseFloat(offer.line_size_percent);
                    }

                    return 0;
                }).reduce((total, offeredLinePercent) => total + offeredLinePercent);

                return line;
            },
            bestPrice: function () {
                return Math.min.apply(Math, this.selectedInsurerOffers.map(function (offer) {
                    return offer.price;
                }));
            },
            waitingPeriod: function() {
                return this.selectedOffers
                    .map(o => o.waiting_period)
                    .reduce(function(a, b) {
                        return Math.max(a, b);
                    }, 0);
            },
            limitAmount() {
                //for captive the limit amount is contract value
                if (this.isCaptiveReinsurance) {
                    return parseFloat(this.submission.contract_value);
                }

                return parseFloat(this.submission.limit_of_liability);
            },
            retainedLimit() {
                if (this.isCaptiveReinsurance && this.captiveInsurerOfferIsOffered) {
                    return parseFloat(this.captiveInsurerOffer.written_line_amount);
                }
                return 0;
            },
            retainedPercent() {
                if (this.isCaptiveReinsurance && this.captiveInsurerOfferIsOffered) {
                    return parseFloat(this.captiveInsurerOffer.written_line_percent);
                }

                return 0;
            },
            reinsuredLimit() {
                if (this.retainedLimit !== null) {
                    return this.lineRequired - this.retainedLimit;
                }
                return null;
            },
            captiveInsurerOffer() {
                return this.submission.offers.find(o => Boolean(o.captive_insurer_company_id));
            },
            captiveInsurerOfferIsOffered() {
                if (this.captiveInsurerOffer) {
                    return this.captiveInsurerOffer.status === 'offered';
                }
            },
            retainedPercentOffer() {
                return this.selectedOffers.find(o => this.isRetainedPercentOffer(o.type));
            },
            percentageRetained() {
                if (this.captiveInsurerOfferIsOffered) {
                    return parseFloat(this.captiveInsurerOffer.written_line_percent)
                }
                return 0;
            },
            writtenColor() {
                return this.percentageWritten > 100 ? 'red' : 'yellow';
            },
            localPricing() {
                return this.pricing;
            },
            availableOffers() {
                return this.submission.offers;
            },
            availableInsurerOffers() {
                return this.submission.offers.filter(offer => !offer.captive_insurer_company_id);
            },
            selectedInsurerOffers() {
                return this.selectedOffers.filter(offer => !offer.captive_insurer_company_id);
            },
            selectedOffers() {
                return this.submission.offers.filter(offer => Boolean(offer.type));
            },
            filteredWaitingPeriods() {
                if (Boolean(this.submission.facility) && Boolean(this.submission.facility.waiting_period_values)) {
                    return this.waitingPeriods.filter(wp => this.submission.facility.waiting_period_values.includes(wp.value));
                }

                return this.waitingPeriods.filter(wp => wp.default);
            },
            captiveInsuranceValid() {
                if (!this.isCaptiveReinsurance) {
                    return true;
                }

                return this.retainedPercentOffer && this.retainedPercentOffer.is_selected;
            },
            insurerLineRequired() {
                return this.lineRequired;
            },
            outstanding: function () {
                return this.lineRequired - this.lineWritten;
            },
            captiveOutstanding: function () {
                return this.insurerLineRequired - this.lineWritten;
            },
            lineRequired: function () {
                return this.limitAmount;
            },
            netMarginRequired() {
                return !this.submission.net_margin && this.selectedOffers.filter(o => o.type === 'percentage_of_net_margin').length > 0;
            },
            grossMarginRequired() {
                return !this.submission.gross_margin && this.selectedOffers.filter(o => o.type === 'percentage_of_gross_margin').length > 0;
            },
            haveMarginFieldDefined() {
                return this.submission.net_margin && this.response === 'percentage_of_net_margin' ||
                    (this.submission.gross_margin && this.response === 'percentage_of_gross_margin')
            },
            totalMargin() {
                if (this.response === 'percentage_of_net_margin') {
                    return this.submission.net_margin;
                } else if (this.response === 'percentage_of_gross_margin') {
                    return this.submission.gross_margin;
                }
            },
            percentageWritten() {
                const self = this;
                const line = this.selectedOffers.map(function (offer) {
                    // check written percent is a valid number, otherwise interpret as zero
                    if (offer.written_line_percent && !isNaN(offer.written_line_percent)) {
                        return self.round(offer.written_line_percent);
                    }

                    return 0;
                }).reduce((total, writtenLine) => total.plus(writtenLine), new Big(0));

                return self.round(line);
            },
            isLineWrittenFull: function () {
                return new Big(this.percentageWritten).eq(100);
            },
            lineWritten: function () {
                return (this.percentageWritten / 100) * this.limitAmount;
            },
            selectedOffersAreValid: function () {
                //if all insurer offer types are not selected
                if (this.selectedOffers.length !== this.availableOffers.length) {
                    return false;
                }

                let valid = true;
                this.selectedOffers.forEach(offer => {
                    // if percent selected is greater than percent offered
                    if ((!offer.written_line_percent && !(this.isRetainedPercentOffer(offer.type) && offer.written_line_percent === 0)) || new Big(offer.written_line_percent).round(2, 0).cmp(new Big(offer.line_size_percent).round(2, 0)) === 1) {
                        valid = false;
                    }
                });

                return valid;
            },
            marginNotProvided() {
                return ((this.netMarginRequired && this.localPricing.net_margin === null) || (this.grossMarginRequired && this.localPricing.gross_margin === null));
            },
            securityLinesValid() {
                return this.selectedOffers.every(offer => {
                    if (this.offerSecurities(offer).length === 0) {
                        return true;
                    }

                    const lines = this.offerLines(offer);

                    const securityIds = lines.map(line => line.security_id);
                    if (securityIds.some(id => !id)) {
                        return false;
                    }
                    if (new Set(securityIds).size !== securityIds.length) {
                        return false;
                    }

                    return lines.length === 1 || this.offerLinesSplitValid(offer);
                });
            },
            nextButtonDisabled() {
                return !this.isLineWrittenFull || !this.selectedOffersAreValid || this.marginNotProvided || !this.hasValidDeclarationLines || !this.firmOrderOffersFormValid || !this.captiveInsuranceValid || !this.securityLinesValid;
            }
        },
        methods: {
            offerSecurities(offer) {
                if ((offer.securities || []).length > 0) {
                    return offer.securities;
                }

                return this.getInsurerSecurities(offer.insurer_company_id ?? offer.insurer?.id);
            },
            getSecuritiesFormatted(securities) {
                return securities.map(security => ({
                    text: security.name,
                    value: security.id
                }));
            },
            offerLines(offer) {
                if (offer.lines && offer.lines.length > 0) {
                    return offer.lines;
                }

                return [
                    {
                        security_id: offer.security_id || null,
                        written_line_percent: offer.written_line_percent,
                        insurer_role: offer.insurer_role || null,
                        manual_subjectivity: offer.manual_subjectivity || null
                    }
                ];
            },
            getOfferLines(offer) {
                if (!offer.lines || offer.lines.length === 0) {
                    this.$set(offer, 'lines', this.offerLines(offer));
                }

                return offer.lines;
            },
            lineSecurityOptions(offer, lineIndex) {
                const siblingSecurityIds = this.offerLines(offer)
                    .filter((line, index) => index !== lineIndex)
                    .map(line => line.security_id)
                    .filter(Boolean);

                return this.offerSecurities(offer)
                    .filter(security => !siblingSecurityIds.includes(security.id));
            },
            canAddSecurityLine(offer) {
                return Boolean(offer.type) &&
                    (parseFloat(offer.written_line_percent) || 0) > 0 &&
                    this.offerSecurities(offer).length > this.offerLines(offer).length;
            },
            lineFlatFee(offer, line) {
                const lines = this.offerLines(offer);

                if (lines.length === 1) {
                    return offer.flat_fee;
                }

                const totalPercent = lines.reduce((sum, l) => sum + (parseFloat(l.written_line_percent) || 0), 0);

                if (!totalPercent) {
                    return 0;
                }

                return (parseFloat(offer.flat_fee) || 0) * ((parseFloat(line.written_line_percent) || 0) / totalPercent);
            },
            addSecurityLine(offer) {
                this.getOfferLines(offer).push({
                    security_id: null,
                    written_line_percent: 0,
                    insurer_role: null,
                    manual_subjectivity: null
                });
            },
            removeSecurityLine(offer, lineIndex) {
                const lines = this.getOfferLines(offer);

                lines.splice(lineIndex, 1);

                if (lines.length === 1) {
                    lines[0].written_line_percent = offer.written_line_percent;
                }

                offer.security_id = lines[0].security_id;
                offer.insurer_role = lines[0].insurer_role || null;
            },
            setLineSecurity(offer, lineIndex, value) {
                this.getOfferLines(offer)[lineIndex].security_id = value;

                if (lineIndex === 0) {
                    offer.security_id = value;
                }
            },
            setLineWrittenPercent(offer, lineIndex, value) {
                this.getOfferLines(offer)[lineIndex].written_line_percent = parseFloat(value) || 0;
            },
            lineWrittenPercentRule(value) {
                const percent = parseFloat(value);

                if (Number.isNaN(percent) || percent <= 0) {
                    return 'Written line percentage must be above zero.';
                }

                return true;
            },
            offerLinesSplitValid(offer) {
                const lines = this.offerLines(offer);

                if (lines.some(line => (parseFloat(line.written_line_percent) || 0) <= 0)) {
                    return false;
                }

                const total = lines.reduce((sum, line) => sum + (parseFloat(line.written_line_percent) || 0), 0);
                const target = parseFloat(offer.written_line_percent) || 0;

                return Math.abs(total - target) < 1e-9;
            },
            resetTypeValues(offerId) {
                const offer = this.availableInsurerOffers.find(offer => offer.id == offerId);

                offer.price = this.isRateOffer(offer.type) ? 0 : null;
                offer.flat_fee = this.isFlatFeeOffer(offer.type) ? 0 : null;
                offer.percentage_of_margin = this.isPercentageOfMarginOffer(offer.type) ? 0 : null;
            },
            isCaptiveInsurer(companyType) {
                return companyType  === 'captive_insurer';
            },
            fetchData: async function () {
                const securityUrl = this.submission?.facility?.id ? `broker/security/list/${this.submission.facility.id}` : `broker/security/list`;
                this.securityTypes = (await axios.get(this.$root.api(securityUrl))).data.data;
                this.cities = (await axios.get(this.$root.api('city'))).data.data;
                this.formatOffers();
                this.loading = false;
            },
            formatOffers() {
                this.submission.offers = this.selectedInsurers.map((insurer) => {
                    let offer = {
                        'insurer': insurer,
                        'captive_insurer': this.isCaptiveInsurer(insurer.company_type) ? insurer : null,
                        'insurer_company_id': insurer.id,
                        'id': insurer.id,
                        'captive_insurer_company_id': this.isCaptiveInsurer(insurer.company_type) ? insurer.id : null,
                        'brokerage': this.defaultBrokerage(),
                        'type': this.isCaptiveInsurer(insurer.company_type) ? 'retained_percent' : null,
                        'price': null,
                        'flat_fee': null,
                        'percentage_of_margin': null,
                        'line_size_percent': 0,
                        'line_size_amount': 0,
                        'written_line_amount': 0,
                        'written_line_percent': 0,
                        'written_line_percent_display': 0,
                        'waiting_period': null,
                        'minimum_price': 0,
                        'default_security_id': null,
                        'securities': this.getInsurerSecurities(insurer.id),
                        'is_selected': true,
                        'is_manual_firm_order': true,
                        'status': 'offered',
                    };
                    if (!this.isCaptiveInsurer(insurer.company_type)) {
                        offer['subjectivities'] = [
                            {
                                name: 'Satisfactory review of underlying documentation',
                                editable: false,
                                selected: false
                            },
                            {
                                name: 'Capacity being available at the time of binding',
                                editable: false,
                                selected: false
                            },
                            {
                                name: 'No material adverse change',
                                editable: false,
                                selected: false
                            },
                            {
                                name: 'Details of prior experience',
                                editable: false,
                                selected: false
                            }
                        ];
                    } else {
                        offer['subjectivities'] = [];
                    }

                    if (this.submission.manual_firm_order_draft_data) {
                        // A draft may hold several rows per insurer, one per security line.
                        const draftRows = this.getAllDraftRowsForInsurer(insurer.id);

                        if (draftRows.length === 0) {
                            return offer;
                        }

                        const draftData = draftRows[0];

                        offer.brokerage = draftData.brokerage;
                        offer.type = draftData.type;
                        offer.price = draftData.price;
                        if (draftData.percentage_of_margin) {
                            offer.percentage_of_margin = draftData.percentage_of_margin
                        }
                        if (draftData.minimum_price) {
                            offer.minimum_price = draftData.minimum_price;
                        }
                        offer.waiting_period = draftData.waiting_period;
                        offer.written_line_amount = draftRows.reduce((sum, row) => sum + (row.written_line_amount || 0), 0);
                        offer.written_line_percent = draftRows.reduce((sum, row) => sum + (row.written_line_percent || 0), 0);
                        offer.flat_fee = draftData.flat_fee;
                        offer.manual_subjectivity = draftData.manual_subjectivity;
                        offer.default_security_id = draftData.security_id;
                        offer.security_id = draftData.security_id;
                        offer.insurer_role = draftData.insurer_role || null;
                        offer.lines = draftRows.map(row => ({
                            security_id: row.security_id,
                            written_line_percent: row.written_line_percent,
                            insurer_role: row.insurer_role || null,
                            manual_subjectivity: row.manual_subjectivity || null
                        }));
                    }

                    if (this.isConvertedFromManualSubmission) {
                        //add feedback
                        offer.feedback = this.submission.manual_submission_tracker.insurers.find(mstIns => mstIns.insurer_company_id === insurer.id)?.feedback || null;
                    }

                    return offer;

                });
            },
            getDataForInsurerFromDraftData(insurer_company_id) {
                return this.submission.manual_firm_order_draft_data.data.offers.find(offer => {
                    return offer.insurer_company_id === insurer_company_id;
                });
            },
            getAllDraftRowsForInsurer(insurer_company_id) {
                return this.submission.manual_firm_order_draft_data.data.offers.filter(offer => {
                    return offer.insurer_company_id === insurer_company_id;
                });
            },
            getInsurerSecurities(insurerCompanyId) {
                if (this.securityTypes.length < 1) {
                    return [];
                }

                return this.securityTypes.filter(security => security.insurer_company_id === insurerCompanyId);
            },
            goToStep(step) {
                if (step === -1) {
                    this.$router.push('/agent/dashboard');
                } else {
                    this.manualFirmOrderStep = step;
                }
            },
            isRateOffer(type) {
                return type === 'rate';
            },
            isFlatFeeOffer(type) {
                return type === 'flat_fee'
            },
            isPercentageOfMarginOffer(type) {
                return ['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(type);
            },
            isRetainedPercentOffer(type) {
                return type === 'retained_percent';
            },
            showHorizontalPriceChange(originalPrice) {

                return this.rateOffersOnly && this.pricing.structure === 'horizontal' && this.pricing.horizontalPrice > originalPrice;
            },
            showHorizontalPercentageChange(originalPercentage) {
                if (this.netMarginRequired && this.marginNotProvided) {
                    return false;
                }

                return this.percentageOfMarginOffersOnly && this.pricing.structure === 'horizontal' && this.pricing.horizontalPercentageOfMargin > originalPercentage;
            },
            goToNextStep() {
                this.resetOffers();
                this.manualFirmOrderStep = 2;
            },
            getOfferTypeName(type) {
                switch (type) {
                    case 'rate':
                        return 'Rate';
                    case 'flat_fee':
                        return 'Flat fee';
                    case 'percentage_of_net_margin':
                        return 'Percentage of net margin';
                    case 'percentage_of_gross_margin':
                        return 'Percentage of gross margin';
                    case 'retained_percent':
                        return 'Retained percent';
                }
            },
            deselectOffersThatAreNotTheSame() {
                const firstOfferType = this.selectedOffers[0]?.type;
                this.selectedOffers.filter(o => o.type !== firstOfferType).forEach((offer) => {
                    offer.type = null;
                })
            },
            switched() {
                if (this.localPricing.structure === 'horizontal') {
                    this.selectedOffers.filter(o => o.type === 'flat_fee').forEach(function (offer) {
                        offer.type = null;
                    });

                    this.deselectOffersThatAreNotTheSame();
                }

                this.calculateHorizontalPrice();
                this.pricing = this.localPricing;
            },
            getMarginByType(type) {
                switch (type) {
                    case 'percentage_of_net_margin':
                        return this.risk.net_margin !== null ? this.localPricing.net_margin : this.submission.net_margin;
                    case 'percentage_of_gross_margin':
                        return this.risk.gross_margin !== null ? this.localPricing.gross_margin : this.submission.gross_margin;
                    default:
                        return null;
                }
            },
            calculateHorizontalPrice() {
                if (this.percentageOfNetMarginOffersOnly) {
                    const net_margin = this.localPricing.net_margin !== null ? this.localPricing.net_margin : this.submission.net_margin;
                    const highestMinimumPrice = Math.max(
                        ...this.selectedOffers
                            .filter(o => o.type === 'percentage_of_net_margin')
                            .map(o => o.minimum_price)
                    );
                    const highestPercentageOfMargin = Math.max(
                        ...this.selectedOffers
                            .filter(o => o.type === 'percentage_of_net_margin')
                            .map(o => o.percentage_of_margin));

                    this.localPricing.horizontalPercentageOfMargin = highestPercentageOfMargin;

                    this.localPricing.horizontalPrice = Math.max(net_margin * (highestPercentageOfMargin / 100), highestMinimumPrice).toFixed(2);
                    this.pricing = this.localPricing;
                    this.$emit('update:pricing', this.localPricing);
                } else if (this.percentageOfGrossMarginOffersOnly) {
                    const gross_margin = this.localPricing.gross_margin !== null ? this.localPricing.gross_margin : this.submission.gross_margin;
                    const highestMinimumPrice = Math.max(
                        ...this.selectedOffers
                            .filter(o => o.type === 'percentage_of_gross_margin')
                            .map(o => o.minimum_price)
                    );
                    const highestPercentageOfMargin = Math.max(
                        ...this.selectedOffers
                            .filter(o => o.type === 'percentage_of_gross_margin')
                            .map(o => o.percentage_of_margin));

                    this.localPricing.horizontalPercentageOfMargin = highestPercentageOfMargin;

                    this.localPricing.horizontalPrice = Math.max(gross_margin * (highestPercentageOfMargin / 100), highestMinimumPrice).toFixed(2);
                    this.$emit('update:pricing', this.localPricing);
                }


                if (this.rateOffersOnly) {
                    const highestPrice = Math.max(...this.selectedOffers.map(o => o.price));

                    this.localPricing.horizontalPrice = highestPrice;
                    this.pricing = this.localPricing;
                    this.$emit('update:pricing', this.localPricing);
                }
            },
            percentageRemaining(offers, column) {
                const self = this;
                const line = offers.map(function (offer) {
                    // check written percent is a valid number, otherwise interpret as zero
                    if (offer[column] && !isNaN(offer[column])) {
                        return self.round(offer[column]);
                    }

                    return 0;
                }).reduce((total, writtenLine) => total + writtenLine, 0);

                return line - 100;
            },
            // Check if percentage is very close to 100% so that we can attempt to update
            // the percentages to make the firm order go through.
            percentagesNeedFixing(newPercentageWritten) {
                const autoFixRange = 0.001;

                return newPercentageWritten !== 100 &&
                    newPercentageWritten > (100 - autoFixRange) &&
                    newPercentageWritten < (100 + autoFixRange);
            },
            // Take the small amount of percent that's either under or over and attempt to add/subtract that between the
            // selected offers without changing the currency value.
            fixPercentages(selectedOffers, column) {
                const self = this;
                const limit = this.limitAmount;
                let remainingPercent = this.percentageRemaining(selectedOffers, column);

                for (let offer of selectedOffers) {
                    if (remainingPercent === 0) {
                        break;
                    }

                    const maxLinePercent = Math.min(offer.line_size_percent, self.round(((offer.written_line_amount / limit) * 100)));

                    let changeAmount = 0;
                    if (remainingPercent < 0) {
                        changeAmount = Math.min(remainingPercent, maxLinePercent - offer[column]);
                    } else if (remainingPercent > 0) {
                        changeAmount = Math.max(remainingPercent, maxLinePercent - offer[column]);
                    }

                    offer[column] -= changeAmount;
                    remainingPercent = this.percentageRemaining(selectedOffers, column);
                }
            },
            round(value, decimalPlaces = this.percentageDecimalPlaces) {
                value = parseFloat(value);

                if (parseFloat(value.toFixed(decimalPlaces)) !== value && !isNaN(value)) {
                    value = value.toFixed(decimalPlaces);
                }

                return Number(value);
            },
            updateOfferPrice(offer) {
                if (this.isPercentageOfMarginOffer(offer.type)) {
                    if (this.localPricing.net_margin !== null && offer.type === 'percentage_of_net_margin') {
                        offer.price = Math.max(this.localPricing.net_margin * (offer.percentage_of_margin / 100), offer.minimum_price).toFixed(2);
                    }
                    if (this.localPricing.gross_margin !== null && offer.type === 'percentage_of_gross_margin') {
                        offer.price = Math.max(this.localPricing.gross_margin * (offer.percentage_of_margin / 100), offer.minimum_price).toFixed(2);
                    }
                }
            },
            resetOffers() {
                const self = this;
                this.selectedOffers.forEach(function (offer) {
                    if (!self.isRateOffer(offer.type) && Boolean(offer.price)) {
                        offer.price = 0;
                    }

                    if (!self.isFlatFeeOffer(offer.type) && Boolean(offer.flat_fee)) {
                        offer.flat_fee = 0;
                    }

                    if (!self.isPercentageOfMarginOffer(offer.type) && (Boolean(offer.minimum_price) || Boolean(offer.percentage_of_margin))) {
                        offer.minimum_price = null;
                        offer.percentage_of_margin = 0;
                    }
                });
            },
            removeInsurer(offer) {
                //delete insurer from insurer list
                Vue.delete(this.selectedInsurersUsersList, offer.insurer_company_id);
                const self = this;

                //delete insurer's offer
                self.$delete(this.submission.offers, this.submission.offers.findIndex(function (submissionOffer) {
                    return submissionOffer.insurer_company_id === offer.insurer_company_id;
                }));
            },
            defaultBrokerage() {
                if (this.submission.facility) {
                    return this.submission.facility.brokerage;
                }

                return 17.5;
            },
            isTypeRate(type) {
                return type === 'rate';
            },
            isTypeFlatFee(type) {
                return type === 'flat_fee';
            },
            isTypePercentageOfMargin(type) {
                return ['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(type);
            },
            hasSameTypes(lines) {
                let lineType = null;

                return lines.every(line => {
                    const lType = line.offer_type || line.type
                    //check line's type
                    if (lineType && lineType !== lType) {
                        return false
                    }
                    lineType = lType;

                    return true;
                });
            },
            getEndLabelColor(percentage) {
                if (percentage === 100) {
                    return 'secondary';
                } else if (percentage > 100) {
                    return 'error'
                }

                return 'amber';
            },
        },
        watch: {
            selectedOffers: {
                handler(offers) {
                    (offers || []).forEach(offer => this.getOfferLines(offer));
                },
                immediate: true
            },
            async percentageWritten(newPercentageWritten) {
                if (this.percentagesNeedFixing(newPercentageWritten)) {
                    await this.$nextTick(); // Fixes issue with written_line_amount being null on some offers.
                    this.fixPercentages(this.selectedOffers, 'written_line_percent');
                }
            },
            'localPricing.net_margin': {
                handler() {
                    if (this.localPricing.net_margin) {
                        const self = this;
                        this.selectedOffers.forEach(function (offer) {
                            self.updateOfferPrice(offer);
                        });
                    }
                }
            },
            'localPricing.gross_margin': {
                handler() {
                    if (this.localPricing.gross_margin) {
                        const self = this;
                        this.selectedOffers.forEach(function (offer) {
                            self.updateOfferPrice(offer);
                        });
                    }
                }
            },
            'submission.offers': {
                handler() {
                    let limit = this.limitAmount;
                    const self = this;

                    this.calculateHorizontalPrice();

                    this.selectedOffers.forEach(function (offer) {
                        offer.line_size_percent = offer.written_line_percent;
                        offer.line_size_mount = offer.written_line_amount;
                        offer.written_line_amount = (offer.written_line_percent / 100) * limit;

                        if (offer.securities.length === 1) {
                            offer.security_id = offer.securities[0].id;
                        }

                        if (!self.rateOffersOnly && !self.percentageOfNetMarginOffersOnly && !self.percentageOfGrossMarginOffersOnly && !self.flatFeeOffersOnly && !self.isCaptiveReinsurance) {
                            self.pricing.structure = 'vertical';
                        }

                        if (offer.type === 'flat_fee') {
                            self.pricing.structure = 'vertical';
                        }

                        self.updateOfferPrice(offer);
                    });
                },
                deep: true
            },
        },
    }
</script>

<style lang="scss" scoped>
.security-line-error {
    color: $color-error;
}

.security-line-percent {
    :deep(input[type="number"]) {
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}

.manual-firm-order-container {
    padding: 32px !important;
}

.captive-offer-table {
    .included-column {
        .x-input {
            justify-content: center;

            :deep(.x-input__slot) {
                margin: 0;

                .x-input--selection-controls__input {
                    width: 24px;
                    height: 24px;
                    margin: 0;

                    .x-input--selection-controls__check {
                        width: 24px;
                        height: 24px;

                        i {
                            font-size: rem(14px);
                        }
                    }
                }
            }

        }
    }
}

.value-percentage-of-margin {
    width: 33%;
}

.value-minimum-price {
    width: 67%;
    margin-left: 5px;
}
</style>
